/** Shared TypeScript interfaces and types for game config, entities, skills, and AI. */
export interface Position {
  x: number;
  y: number;
}

export interface Velocity {
  x: number;
  y: number;
}

/** Shared damage tuning shape used by CombatTuningConfig defaults and per-attack overrides. */
export interface DamageConfig {
  baseDamage: number;
  critChanceBase: number;
  critChancePerSkillLevel: number;
  critMultiplier: number;
  minimumDamage: number;
  backstabMultiplier: number;
  backstabAngle: number;
}

// --- Config Types (match JSON schemas in src/data/) ---

export interface MovementConfig {
  walk: {
    speed: number;
  };
  run: {
    speedMultiplier: number;
    staminaMax: number;
    staminaDrainPerSecond: number;
    staminaRechargePerSecond: number;
    staminaRechargeDelay: number;
  };
  dash: {
    speed: number;
    distance: number;
    durationMs: number;
    iFrames: boolean;
    iFramesCancelledByAttack: boolean;
    dashAttackCoyoteFrames: number;
    dashAttackDamageMultiplier: number;
    charges: {
      base: number;
      max: number;
      refillRequiresFullStamina: boolean;
      refillTimeMs: number;
    };
  };
  upgrades: {
    extraDashCharge: {
      description: string;
      maxStacks: number;
    };
    increasedStamina: {
      description: string;
      bonusPerStack: number;
      maxStacks: number;
    };
    instantRefill: {
      description: string;
      rechargeMultiplier: number;
    };
    simultaneousRefill: {
      description: string;
      refillRequiresFullStamina: boolean;
    };
  };
}

export interface HitstopEntry {
  hitstopFrames: number;
}

export interface CombatTuningConfig {
  hitstop: Record<string, HitstopEntry>;
  knockback: {
    baseDistance: number;
    attackMultipliers: Record<string, number>;
    wallBonusDamageMultiplier: number;
    enemyCollisionStaggerMs: number;
    velocityMultiplier: number;
    durationMs: number;
    enemyCollisionDistance: number;
    playerKnockbackMultiplier?: number;
  };
  screenEffects: {
    playerHit: {
      shakeIntensity: number;
      shakeDurationMs: number;
      redFlashAlpha: number;
      redFlashDurationMs: number;
    };
    playerDeath: {
      deathLingerMs: number;
      rumbleIntensity: number;
      rumbleDurationMs: number;
      fadeToBlackMs: number;
      holdBlackMs: number;
      dissolveFlashMs: number;
      spawnProtectionMs: number;
      retreatRadius: number;
      retreatSpeed: number;
      retreatDurationMs: number;
      deathFlashAlpha: number;
      deathFlashScale: number;
      deathFlashMs: number;
      deathSpikes: {
        count: number;
        width: number;
        texHeight: number;
        minLength: number;
        maxLength: number;
        shimmerHz: number;
        shimmerDepth: number;
      };
      deathShockwaveRadius: number;
      deathShockwaveSpeed: number;
      deathShockwaveDurationMs: number;
      deathStunMs: number;
      deathHoldMs: number;
      iris: {
        closeDurationMs: number;
      };
      shatter: {
        particleCount: number;
        particleRadius: number;
        minSpeed: number;
        maxSpeed: number;
        durationMs: number;
        fadeStartRatio: number;
        sparkle: {
          particleCount: number;
          particleRadius: number;
          minSpeed: number;
          maxSpeed: number;
          durationMs: number;
          fadeStartRatio: number;
          delayMs: number;
        };
      };
      coalesce: {
        particleCount: number;
        particleRadius: number;
        scatterRadius: number;
        durationMs: number;
      };
      fadeMultiplier: number;
      respawnDelayRatio: number;
    };
    heavyHitLanded: {
      shakeIntensity: number;
      shakeDurationMs: number;
    };
    dash: {
      motionBlurStrength: number;
      speedLineAlpha: number;
    };
    conductorProximity: {
      chromaticAberrationStrength: number;
      distortionFrequency: number;
      triggerRadius: number;
    };
    lowHp: {
      vignetteStrength: number;
      pulseFrequencyHz: number;
      threshold: number;
    };
  };
  camera: {
    combatZoomOut: number;
    combatZoomRadius: number;
    dashLeadDistance: number;
    dashLeadLerpSpeed: number;
    deathZoomSpeed: number;
  };
  input: {
    bufferWindowFrames: number;
    comboWindowMs: number;
    attackChainLength: number;
    dashCancelsRecovery: boolean;
    windupMovementFactor: number;
  };
  damage: DamageConfig;
  attackVisuals: Record<string, AttackVisualConfig>;
  player: {
    maxHp: number;
  };
  attackTimings: Record<string, {
    windupMs: number;
    activeMs: number;
    recoveryMs: number;
  }>;
  damageNumbers: {
    floatDistance: number;
    floatDurationMs: number;
    randomOffsetRange: number;
    dealtColor: string;
    receivedColor: string;
    baseFontSizePx: number;
    fontScalePerRatio: number;
    minFontSizePx: number;
    maxFontSizePx: number;
    missProximityRadius: number;
    staggerColor: string;
    zigzagAmplitude: number;
    zigzagCycles: number;
    labelDelayMs: number;
  };
  wanderCooldown: {
    baseMs: number;
    randomRangeMs: number;
  };
  hitbox: {
    width: number;
    height: number;
    offset: number;
  };
  summons: {
    aggroLeakPct: number;
    aggroLeakRequiresHide: boolean;
  };
  skillStamina: {
    defaultCost: number;
  };
}

export interface AttackVisualConfig {
  arcRadius: number;
  sweepDegrees: number;
  color: number;
  lineWidth: number;
}

// --- Effectiveness Multiplier (rewards competent skill use with XP bonuses) ---

export type EffectivenessSignal =
  | 'hitLanded' | 'backstab' | 'stealthAttack'
  | 'multiHit' | 'killSecured'
  | 'enemyHasLOS' | 'dangerPresent';

export interface EffectivenessProfile {
  signals: Partial<Record<EffectivenessSignal, number>>;
  cap?: number;  // defaults to global defaultCap (2.0)
}

export interface RecordUseOptions {
  failed?: boolean;
  toughEnemy?: boolean;
  danger?: number;
  stepFraction?: number;
  // Effectiveness signals
  hitLanded?: boolean;
  backstab?: boolean;
  stealthAttack?: boolean;
  multiHit?: boolean;
  killSecured?: boolean;
  enemyHasLOS?: boolean;
  dangerPresent?: boolean;
}

// --- V4 Skill / Attunement Types (single-path tree, ADR-037) ---

/** The four V4 node types that define how a node behaves in the tree. */
export type V4NodeType = 'active' | 'evolution' | 'upgrade' | 'passive';

/** Human-readable labels for V4 node types. */
export const NODE_TYPE_LABELS: Record<string, string> = {
  active: 'Active Skill',
  evolution: 'Evolution',
  upgrade: 'Upgrade',
  passive: 'Passive',
};

/** Cooldown tier determines the skill's place in a rotation: fast filler → devastating finisher. */
export type CooldownTier = 'fast' | 'medium' | 'long' | 'very_long';

/** A single node in a V4 attunement tree. All node types share this shape. */
export interface V4NodeDefinition {
  id: string;
  name: string;
  nodeType: V4NodeType;
  parentId: string;           // every node has exactly one parent (foundation's parentId is 'root')
  description: string;        // mechanical description shown in popup
  flavorText: string;         // in-world flavor shown in popup
  xpThreshold: number;        // tree XP required to unlock this node
  path?: string;              // branch identity (e.g. "hurtmore", "grapple") — set after Fork 1

  // Fizzle override — when set, this node uses its own base success rate instead of the global default
  baseSuccessChance?: number;

  // Active-specific fields
  cooldownTier?: CooldownTier;

  // Evolution-specific fields
  replacesId?: string;        // which Active this evolution replaces in the hotbar

  // Upgrade-specific fields
  enhancesId?: string;        // which Active this upgrade improves
  effect?: Record<string, number | boolean>;  // stat modifications (e.g. { "damage": 1.3, "ignoreKnockbackResistance": true })

  // Passive-specific fields — effect field reused for stat bonuses
  // Passives with no effect field are conditional (description explains the trigger)

  // Effectiveness profile — rewards contextually competent use with XP bonuses
  effectiveness?: EffectivenessProfile;
}

/** Apotheosis node — like V4NodeDefinition but supports multiple parents (convergence). */
export interface V4ApotheosisDefinition {
  id: string;
  name: string;
  nodeType: 'active';
  parentIds: string[];        // multiple parents — player needs any one completed
  description: string;
  flavorText: string;
  xpThreshold: number;
  cooldownTier: 'very_long';
  path: string;               // which Fork 1 path this Apotheosis belongs to

  // Effectiveness profile — rewards contextually competent use with XP bonuses
  effectiveness?: EffectivenessProfile;
}

/** Whether a node is a fork point (has multiple Active children that require a choice). */
export interface V4ForkDefinition {
  forkId: string;             // the parent node ID where the fork happens
  options: string[];          // IDs of the Active nodes the player chooses between
}

/** Complete V4 attunement tree definition. */
export interface V4TreeDefinition {
  version: number;
  id: string;
  name: string;
  foundation: V4NodeDefinition;
  nodes: V4NodeDefinition[];         // all non-foundation, non-apotheosis nodes (flat array)
  apotheosis: V4ApotheosisDefinition[];  // convergence nodes (one per Fork 1 path)
  forks: V4ForkDefinition[];         // explicit fork point declarations
  forkFlavorText?: string[];         // random flavor lines shown at branch points
}

/** V4 tree progress — tracks XP and fork choices (everything else auto-derives). */
export interface V4TreeProgress {
  xp: number;
  uses: number;
  forkChoices: Record<string, string>;  // forkId → chosen node ID
}

/** V4 hotbar configuration — 4 slots, each holding an Active skill ID or null. */
export interface V4HotbarConfig {
  slots: [string | null, string | null, string | null, string | null];
}

/** Fizzle config — newly unlocked skills have a success chance that scales with mastery XP. */
export interface FizzleConfig {
  defaultBaseSuccessChance: number;  // success rate at 0 mastery when node has no override (e.g. 0.4 = 40%)
  masteryRatio: number;              // fraction of gap to next tier boundary that constitutes full mastery
}

/** V4 skill progression config — replaces V3 SkillProgressionConfig. */
export interface V4SkillProgressionConfig {
  hotbar: {
    slotCount: number;            // always 4
    foundationSkills: string[];   // skills auto-slotted at game start
  };
  respec: {
    location: string;
    cost: string;
  };
  xpGain: {
    baseXpPerUse: number;
    toughEnemyMultiplier: number;
    failedUseMultiplier: number;
    dangerMaxEnemies: number;
    dangerXpFloor: number;
    dangerPresenceBase: number;
    dangerDistanceRadius: number;
    dangerDistanceFloor: number;
    dangerSigmoidSteepness: number;
    dangerSigmoidMidpoint: number;
    thresholdScale: number;
    relevanceFloor: number;
    fizzle: FizzleConfig;
  };
  skillTrees: Record<string, V4TreeDefinition>;
}

/** XP threshold boundaries for a single tier (threshold, transcendent, or apotheosis). */
export interface TierThresholds {
  base: number;
  second: number;
  third?: number;
}

export interface HideTuningConfig {
  maxCooldownMs: number;
  minCooldownMs: number;
  cooldownDecayRate: number;
  baseSuccessChance: number;
  maxSuccessChance: number;
  xpForMaxChance: number;
  proximityCheckDistancePx: number;
  sustainedXpIntervalMs: number;
  dangerGateThreshold: number;
  dangerSuccessPenalty: number;
  dangerSuccessFloor: number;
  lowDangerBoostZero: number;
  lowDangerBoostOne: number;
}

export interface StealthTuningConfig {
  cooldownMs: number;
  baseSuccessChance: number;
  maxSuccessChance: number;
  xpForMaxChance: number;
  proximityCheckDistancePx: number;
  movementSpeedMultiplier: number;
  baseSteps: number;
  stepsPerXp: number;
  dangerSuccessPenalty: number;
  dangerSuccessFloor: number;
  lowDangerBoostZero: number;
  lowDangerBoostOne: number;
  aggroWipe: {
    minRadiusPx: number;
    maxRadiusPx: number;
    xpForMaxRadius: number;
  };
}

export interface AttackTuningConfig {
  timing: { windupMs: number; activeMs: number; recoveryMs: number; cooldownMs?: number };
  damage: DamageConfig;
  hitstop: { frames: number };
  knockback: { multiplier: number };
  visuals: { arcRadius: number; sweepDegrees: number; color: number; lineWidth: number };
  skillId?: string;
  stagger?: { durationMs: number };
  maxTargets?: number;
  hitbox?: { width: number; height: number; offset: number };
}

export interface PlayerHideState {
  isHidden: boolean;
  successChance: number;
  proximityDistancePx: number;
  proximityCheck: () => boolean;
  /** When true, sound-cone detection is suppressed regardless of hide state (e.g. Ghost Dash). */
  suppressSoundDetection?: boolean;
}

// --- Enemy Skill Types ---

export interface EnemySkillDefinition {
  id: string;
  type: 'melee' | 'dash' | 'ranged' | 'area' | 'bolt' | 'block' | 'buff' | 'backup';
}

// --- Skill Effect Declarations (data-driven status effect application, ADR-087) ---

/** Trigger events that cause a declared status effect to fire. */
export type EffectTrigger = 'activation' | 'hit' | 'holdEntry' | 'deactivation' | 'expiry';

/** How a parameter value is resolved at runtime. */
export type ParamSource =
  | { type: 'static'; value: number }
  | { type: 'resolverAdditive'; skillId: string; key: string }
  | { type: 'resolverMax'; skillId: string; key: string }
  | { type: 'resolverMultiplicative'; skillId: string; key: string; base?: number }
  | { type: 'treePassive'; tree: string; key: string; mode: 'sum' | 'max' }
  | { type: 'computed'; base: ParamSource; bonus: ParamSource; op: 'add' | 'multiply' };

/** Conditions that must ALL pass (AND logic) before the effect is applied. */
export type EffectCondition =
  | { type: 'immunityCheck'; effectId: string }
  | { type: 'hasUpgrade'; skillId: string; key: string }
  | { type: 'paramGt0'; param: ParamSource }
  | { type: 'chargeRatio'; gte: number }
  | { type: 'targetType'; value: 'ranged' | 'melee' }
  | { type: 'defenseType'; value: string }
  | { type: 'probability'; chance: ParamSource };

/** Parameters passed to StatusEffectTracker.apply(). */
export interface DeclaredEffectParams {
  durationMs: ParamSource;
  intensity?: ParamSource;
  stacks?: ParamSource;
  stackingRuleOverride?: 'refresh' | 'intensity' | 'duration' | 'independent' | 'override';
}

/** Who the effect targets. Defaults to 'enemy' if omitted. */
export type EffectTarget = 'enemy' | 'self';

/** A single status effect application within a skill declaration. */
export interface EffectDeclaration {
  effectId: string;
  trigger: EffectTrigger;
  params: DeclaredEffectParams;
  conditions?: EffectCondition[];
  /** Defaults to 'enemy'. Self-buffs (ironbody, titan, grit DR) use 'self'. */
  target?: EffectTarget;
  /** Source tag for DoT coexistence (ADR-066). Defaults to skillId if omitted. */
  source?: string;
}

/** One skill's complete status effect declaration. */
export interface SkillEffectDeclaration {
  skillId: string;
  effects: EffectDeclaration[];
  imperative?: true;
  notes?: string;
}

/** Top-level shape of skill-effect-declarations.json. */
export interface SkillEffectDeclarationsFile {
  declarations: SkillEffectDeclaration[];
}

export interface EnemyStaminaConfig {
  max: number;
  regenPerSec: number;
}

export interface EnemySprintConfig {
  multiplier: number;
  stamina: number;
  drainPerSec: number;
  rechargePerSec: number;
  exhaustedPenalty: number;
  exhaustedCooldown: number;
}

export type EnemyWeight = 'light' | 'medium' | 'heavy' | 'immovable';

export interface EnemyArchetypeConfig {
  id: string;
  name: string;
  baseHp: number;
  baseDamage: number;
  moveSpeed: number;
  chaseSpeed: number;
  wanderRadius: number;
  canRetreat: boolean;
  retreatThreshold: number;
  panicOnLeaderDeath: boolean;
  panicDurationMs: number;
  detectionRadiusMultiplier: number;
  aggroBuildRate: number;
  aggroDecayRate: number;
  aggroHoldDuration: number;
  chaseThreshold: number;
  disengageThreshold: number;
  knockbackResistance: number;
  weight: EnemyWeight;
  detectsMagicCharge: boolean;
  visionAngle: number; // degrees — full cone width (e.g. 120 = 60° each side of facing)
  soundConeAngle: number; // degrees — full cone width per side cone (e.g. 60 = 30° each side of perpendicular)
  soundConeRange: number; // pixels — detection range for side sound cones
  attackType?: string;
  patrolRoute?: boolean;
  patrolDurationMs?: number;
  wanderDurationMs?: number;
  patrolRadius?: number;
  passiveWakeMinMs?: number;  // min interval before passive→patrol wake-up (-1 = never wake)
  passiveWakeMaxMs?: number;  // max interval before passive→patrol wake-up
  retreatBehavior?: string;
  backupRadius?: number;
  repositionBehavior?: string;
  preferredDistance?: number;
  buffRadius?: number;
  buffDamageMultiplier?: number;
  buffSpeedMultiplier?: number;
  spriteKey?: string;
  // Skill system (ADR-033) — all archetypes have at least one skill
  skills: EnemySkillDefinition[];
  skillTuning?: Record<string, Record<string, unknown>>;
  stamina?: EnemyStaminaConfig;
  sprint?: EnemySprintConfig;
  corpseDurationMs?: number;
  placeholderColor?: string;
  separationRadius?: number;  // per-archetype override for pairwise separation distance (px)
  separationForce?: number;   // per-archetype override for pairwise separation push strength
}
